import React, { useEffect } from "react";
import { Box, Card, Table, TableHead, TableRow, TableCell, TableBody, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import dayjs from "dayjs";

import { fetchMyUnclaimedPackages, selectMyUnclaimedPackages } from "../../redux/slices/unclaimedPackagesSlice";
import PageHeader from "../../components/common/PageHeader";
import EmptyState from "../../components/common/EmptyState";
import SkeletonLoader from "../../components/common/SkeletonLoader";

// Purely informational — nothing to click or act on here. A package shows
// up only because an admin manually tagged it (UnclaimedPackage.possibleCustomer)
// as possibly belonging to this customer; it disappears on its own the
// moment the customer creates a shipment whose item tracking number matches
// (shipmentItemController.matchAndClaimForItem deletes the record and sets
// the matched item to "received", carrying over its D/R/weight/CBF), so
// there's no "claim" button or status column needed — anything still shown
// here is by definition still outstanding.
const CustomerUnclaimedPackages = () => {
    const dispatch = useDispatch();
    const packages = useSelector(selectMyUnclaimedPackages);
    const loading = useSelector((state) => state.unclaimedPackages.loading);

    useEffect(() => {
        dispatch(fetchMyUnclaimedPackages());
    }, [dispatch]);

    return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
            <PageHeader
                title="Unclaimed Packages"
                subtitle="Packages that arrived without a matching shipment — create a shipment with the matching tracking number to claim one automatically."
            />

            <Card>
                {loading ? (
                    <SkeletonLoader rows={5} />
                ) : packages.length === 0 ? (
                    <EmptyState
                        icon="📦"
                        title="No unclaimed packages"
                        description="Packages we're holding that look like they might be yours will show up here."
                    />
                ) : (
                    <Box sx={{ overflowX: "auto" }}>
                        <Table sx={{ minWidth: 480 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Tracking #</TableCell>
                                    <TableCell>Carrier</TableCell>
                                    <TableCell>Received</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {packages.map((p) => (
                                    <TableRow key={p._id}>
                                        <TableCell sx={{ fontFamily: "monospace", fontSize: 12 }}>
                                            {p.trackingNumber}
                                        </TableCell>
                                        <TableCell>{p.carrier || "—"}</TableCell>
                                        <TableCell sx={{ fontSize: 12, color: "text.secondary" }}>
                                            {p.receivedDate ? dayjs(p.receivedDate).format("MMM D, YYYY") : "—"}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </Box>
                )}
            </Card>

            {packages.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                    Create a shipment with one of these tracking numbers and it'll be matched and removed from this
                    list automatically.
                </Typography>
            )}
        </Box>
    );
};

export default CustomerUnclaimedPackages;
